import * as React2 from 'react';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { unwrapData, ApiError } from '@authe/fe-commons/api';
import { createLogger } from '@authe/fe-commons/logging';
import { createScopedStorage } from '@authe/fe-commons/storage';
import { notifySuccess, notifyError, Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DropdownMenu, DropdownMenuTrigger, Button, cn, DropdownMenuContent, SearchIcon, Input, DropdownMenuLabel, DropdownMenuItem, CheckIcon, DropdownMenuSeparator, Skeleton, ErrorState, EmptyState, Badge, StarIcon, ShareIcon, TrashIcon, Alert, AlertTitle, AlertDescription, Field, Select, FormControl, SelectTrigger, SelectValue, SelectContent, SelectItem, DialogFooter, notifyInfo, Label } from '@authe/ui-core';

// src/config.ts
var SAVED_VIEWS_PACKAGE = "saved-views";
var SV_ACTIVE_SUFFIX = "active";
var SV_DRAFT_SUFFIX = "draft";
var SV_URL_PARAM = "view";
var SV_MAX_NAME_LENGTH = 80;
var SV_MAX_SHARE_URL_LENGTH = 2048;

// src/model/view.ts
var VIEW_SCOPES = ["user", "tenant"];
var SCOPE_LABELS = {
  user: "Personal",
  team: "Team",
  tenant: "Shared",
  org: "Organization",
  customer: "Customer",
  system: "Default"
};
function isViewScope(value) {
  return typeof value === "string" && ["user", "team", "tenant", "org", "customer", "system"].includes(value);
}
function newViewId() {
  const rand = Math.random().toString(36).slice(2, 10);
  return `sv_${Date.now().toString(36)}${rand}`;
}
function emptyView(tableId, scope = "user") {
  const now = Date.now();
  return {
    id: newViewId(),
    name: "",
    tableId,
    scope,
    state: {},
    createdAt: now,
    updatedAt: now
  };
}
function isEmptyState(state) {
  return !state.columns?.length && !state.filters?.length && !state.sort?.length && !state.pinnedRows?.length && !state.density && !state.extra;
}

// src/model/share.ts
var SHARE_TARGETS = ["public", "user", "team", "tenant", "org"];
var SHARE_TARGET_LABELS = {
  public: "Anyone with the link",
  user: "Specific people",
  team: "A team",
  tenant: "Everyone in this tenant",
  org: "Everyone in this organization"
};
var VIEW_DATA_MODES = ["static", "dynamic"];
var VIEW_DATA_MODE_LABELS = {
  static: "Snapshot (frozen at share time)",
  dynamic: "Live (updates after sharing)"
};
var CATALOG_RESOLUTIONS = [
  "viewer",
  "owner_snapshot",
  "defaults"
];
var CATALOG_RESOLUTION_LABELS = {
  viewer: "Viewer's catalog (their tags/labels)",
  owner_snapshot: "My catalog, frozen (owner snapshot)",
  defaults: "Global defaults only"
};
function isExternalTarget(target) {
  return target === "public";
}

// src/serialization/serialize.ts
function toBase64Url(bytes) {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  const b64 = typeof btoa !== "undefined" ? btoa(binary) : Buffer.from(bytes).toString("base64");
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function fromBase64Url(value) {
  const b64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const binary = typeof atob !== "undefined" ? atob(b64) : Buffer.from(b64, "base64").toString("binary");
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}
function encodeViewState(state) {
  let payload = state;
  if (state.extra && ("snapshot" in state.extra || "catalogSnapshot" in state.extra)) {
    const { snapshot: _s, catalogSnapshot: _c, ...restExtra } = state.extra;
    payload = { ...state, extra: Object.keys(restExtra).length ? restExtra : void 0 };
  }
  return toBase64Url(new TextEncoder().encode(JSON.stringify(payload)));
}
function decodeViewState(token) {
  try {
    const json = new TextDecoder().decode(fromBase64Url(token));
    const parsed = JSON.parse(json);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}
function toSearchParams(search) {
  return typeof search === "string" ? new URLSearchParams(search) : search;
}
function readViewStateFromUrl(search, param = SV_URL_PARAM) {
  const token = toSearchParams(search).get(param);
  return token ? decodeViewState(token) : null;
}
function applyViewStateToUrl(url, state, param = SV_URL_PARAM) {
  const isAbsolute = /^[a-z]+:\/\//i.test(url);
  const base = isAbsolute ? void 0 : "http://_";
  const parsed = new URL(url, base);
  parsed.searchParams.set(param, encodeViewState(state));
  return isAbsolute ? parsed.toString() : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

// src/serialization/shareToken.ts
var ALWAYS_RESERVED = ["index.html"];
function segmentsOf(path) {
  return path.split("/").filter(Boolean);
}
function readShareTokenFromUrl(location2, options = {}) {
  const { basePath, queryParams = ["token"] } = options;
  const params = new URLSearchParams(location2.search);
  for (const name of queryParams) {
    const value = params.get(name);
    if (value) return value;
  }
  const segments = segmentsOf(location2.pathname);
  const last = segments[segments.length - 1];
  if (!last) return null;
  const reserved = /* @__PURE__ */ new Set([...ALWAYS_RESERVED, ...segmentsOf(basePath ?? "")]);
  if (reserved.has(last)) return null;
  try {
    return decodeURIComponent(last);
  } catch {
    return null;
  }
}

// src/data/source.ts
function createInMemorySavedViewsSource(seed = []) {
  const store = /* @__PURE__ */ new Map();
  for (const view of seed) store.set(view.id, view);
  const listViews = async ({ tableId, scope }) => [...store.values()].filter((v) => v.tableId === tableId && (scope ? v.scope === scope : true)).sort((a, b) => a.name.localeCompare(b.name));
  const links = /* @__PURE__ */ new Map();
  let linkSeq = 0;
  const randomToken = () => `tok_${(++linkSeq).toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  const clearDefaults = (tableId, scope, except) => {
    for (const view of store.values()) {
      if (view.tableId === tableId && view.scope === scope && view.id !== except && view.isDefault) {
        store.set(view.id, { ...view, isDefault: false });
      }
    }
  };
  return {
    async createShareLink(viewId, input) {
      const token = randomToken();
      const link = {
        id: `svl_${token}`,
        token,
        viewId,
        target: input.target,
        payloadVisibility: input.target === "public" ? "public" : "private",
        dataMode: input.dataMode ?? "static",
        catalogResolution: input.catalogResolution ?? "viewer",
        hasPassword: Boolean(input.password),
        expiresAt: input.expiresAt ?? null,
        maxViews: input.maxViews ?? null,
        viewCount: 0,
        revoked: false,
        targetUserIds: input.targetUserIds ?? [],
        targetTeamIds: input.targetTeamIds ?? [],
        shareUrl: `/shares/${token}`,
        createdAt: Date.now()
      };
      links.set(token, link);
      return link;
    },
    async listShareLinks(viewId) {
      return [...links.values()].filter((l) => l.viewId === viewId);
    },
    async updateShareLink(token, input) {
      const existing = links.get(token);
      if (!existing) throw new Error(`unknown share token: ${token}`);
      const next = {
        ...existing,
        expiresAt: input.expiresAt === void 0 ? existing.expiresAt : input.expiresAt,
        maxViews: input.maxViews === void 0 ? existing.maxViews : input.maxViews,
        revoked: input.revoked ?? existing.revoked,
        hasPassword: input.password === void 0 ? existing.hasPassword : Boolean(input.password)
      };
      links.set(token, next);
      return next;
    },
    async revokeShareLink(token) {
      const existing = links.get(token);
      if (existing) links.set(token, { ...existing, revoked: true });
    },
    async resolveShare(token) {
      const link = links.get(token);
      if (!link || link.revoked) throw new Error("link not found or revoked");
      links.set(token, { ...link, viewCount: link.viewCount + 1 });
      const view = store.get(link.viewId);
      return {
        view: view ?? { id: link.viewId },
        link: {
          token: link.token,
          target: link.target,
          hasPassword: link.hasPassword,
          viewCount: link.viewCount + 1,
          payloadVisibility: link.payloadVisibility
        },
        dataMode: link.dataMode,
        catalogResolution: link.catalogResolution,
        payload: view?.state ? { state: view.state } : null,
        payloadUrl: null,
        payloadCdnPath: null,
        dynamicSeed: null,
        warnings: []
      };
    },
    list: listViews,
    async listWithMeta(query) {
      return { views: await listViews(query), truncated: false };
    },
    async get(id) {
      const view = store.get(id);
      if (!view) throw new Error(`unknown saved view: ${id}`);
      return view;
    },
    async create(view) {
      if (view.isDefault) clearDefaults(view.tableId, view.scope, view.id);
      store.set(view.id, view);
      return view;
    },
    async update(view) {
      const next = { ...view, updatedAt: Date.now() };
      if (next.isDefault) clearDefaults(next.tableId, next.scope, next.id);
      store.set(next.id, next);
      return next;
    },
    async remove(id) {
      store.delete(id);
    },
    async setDefault(tableId, scope, id) {
      clearDefaults(tableId, scope, id ?? void 0);
      if (id) {
        const view = store.get(id);
        if (view) store.set(id, { ...view, isDefault: true, updatedAt: Date.now() });
      }
    }
  };
}
var SavedViewsContext = React2.createContext(null);
function SavedViewsProvider({
  source,
  identity,
  children
}) {
  const { app, customerId, orgId, tenantId, userId } = identity;
  const value = React2.useMemo(
    () => ({ source, identity: { app, customerId, orgId, tenantId, userId } }),
    [source, app, customerId, orgId, tenantId, userId]
  );
  return /* @__PURE__ */ jsx(SavedViewsContext.Provider, { value, children });
}
function useOptionalSavedViews() {
  return React2.useContext(SavedViewsContext);
}
var log = createLogger(`${SAVED_VIEWS_PACKAGE}:api-source`);
function toMs(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n < 1e12 ? Math.round(n * 1e3) : n;
}
function toMsOrNull(value) {
  if (value === null || value === void 0 || value === "") return null;
  const ms = toMs(value);
  return ms || null;
}
function toSeconds(value) {
  if (value === null || value === void 0) return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n >= 1e12 ? Math.round(n / 1e3) : Math.round(n);
}
var MAX_LIST_PAGES = 20;
function viewFromWire(raw) {
  return {
    id: raw.id,
    name: raw.name ?? "",
    tableId: raw.table_id ?? raw.tableId ?? "",
    scope: isViewScope(raw.scope) ? raw.scope : "user",
    owner: raw.owner_user_id ?? raw.owner ?? null,
    isDefault: Boolean(raw.is_default ?? raw.isDefault ?? false),
    state: raw.view_state ?? raw.state ?? {},
    dataMode: raw.data_mode ?? raw.dataMode ?? void 0,
    payloadVisibility: raw.payload_visibility ?? raw.payloadVisibility ?? void 0,
    catalogResolution: raw.catalog_resolution ?? raw.catalogResolution ?? void 0,
    dynamicSeed: raw.dynamic_seed ?? raw.dynamicSeed ?? null,
    snapshotS3Key: raw.snapshot_s3_key ?? raw.snapshotS3Key ?? void 0,
    createdAt: toMs(raw.created_at ?? raw.createdAt),
    updatedAt: toMs(raw.updated_at ?? raw.updatedAt)
  };
}
function viewToWire(view) {
  const body = {};
  if (view.tableId !== void 0) body.table_id = view.tableId;
  if (view.name !== void 0) body.name = view.name;
  if (view.scope !== void 0) body.scope = view.scope;
  if (view.isDefault !== void 0) body.is_default = view.isDefault;
  if (view.state !== void 0) body.view_state = view.state;
  if (view.dataMode !== void 0) body.data_mode = view.dataMode;
  if (view.payloadVisibility !== void 0) body.payload_visibility = view.payloadVisibility;
  if (view.catalogResolution !== void 0) body.catalog_resolution = view.catalogResolution;
  if (view.dynamicSeed !== void 0) body.dynamic_seed = view.dynamicSeed;
  if (view.snapshot !== void 0) body.snapshot = view.snapshot;
  if (view.catalogSnapshot !== void 0) body.catalog_snapshot = view.catalogSnapshot;
  return body;
}
function linkFromWire(raw) {
  return {
    id: raw.id,
    token: raw.token,
    viewId: raw.view_id,
    target: raw.target,
    payloadVisibility: raw.payload_visibility,
    dataMode: raw.data_mode,
    catalogResolution: raw.catalog_resolution,
    hasPassword: Boolean(raw.has_password),
    expiresAt: toMsOrNull(raw.expires_at),
    maxViews: raw.max_views ?? null,
    viewCount: raw.view_count ?? 0,
    revoked: Boolean(raw.revoked),
    targetUserIds: raw.target_user_ids ?? [],
    targetTeamIds: raw.target_team_ids ?? [],
    shareUrl: raw.share_url ?? void 0,
    // The backend says WHY a link has no copyable URL (no viewer base wired for
    // that audience) instead of omitting the field and letting the dialog
    // invent `${location.origin}/shares/{token}` — a host that serves no viewer.
    shareUrlUnavailable: raw.share_url_unavailable ?? void 0,
    createdAt: toMsOrNull(raw.created_at),
    createdBy: raw.created_by ?? null
  };
}
function createApiSavedViewsSource(client, options = {}) {
  const base = options.basePath ?? "/saved-views";
  const sharesBase = options.sharesBasePath ?? "/shares";
  const cdnBaseUrl = options.cdnBaseUrl?.replace(/\/+$/, "");
  const viewPath = (id) => `${base}/${encodeURIComponent(id)}`;
  const viewSharesPath = (id) => `${viewPath(id)}/shares`;
  const sharePath = (token) => `${sharesBase}/${encodeURIComponent(token)}`;
  const resolvePath = (token) => options.authenticatedResolve ? `${sharePath(token)}/resolve` : sharePath(token);
  const currentUserId = options.currentUserId ?? null;
  async function listPage(query) {
    const all = [];
    let cursor;
    let truncated = false;
    let pagesRead = 0;
    for (let page = 0; page < MAX_LIST_PAGES; page++) {
      pagesRead = page + 1;
      const res = await client.get(base, {
        query: {
          table_id: query.tableId,
          ...query.scope ? { scope: query.scope } : {},
          ...cursor ? { cursor } : {}
        }
      });
      const data = unwrapData(res);
      if (Array.isArray(data)) {
        all.push(...data);
        cursor = void 0;
        break;
      }
      all.push(...data?.items ?? []);
      truncated = truncated || Boolean(data?.truncated);
      cursor = data?.next_cursor ?? void 0;
      if (!cursor) break;
    }
    if (cursor && pagesRead >= MAX_LIST_PAGES) truncated = true;
    return { views: all.map(viewFromWire), truncated };
  }
  async function listAll(query) {
    return (await listPage(query)).views;
  }
  return {
    list: listAll,
    listWithMeta: listPage,
    async get(id) {
      const res = await client.get(viewPath(id));
      const data = unwrapData(res);
      return viewFromWire(data?.view ?? data);
    },
    async create(view) {
      return viewFromWire(unwrapData(await client.post(base, viewToWire(view))));
    },
    async update(view) {
      return viewFromWire(
        unwrapData(await client.patch(viewPath(view.id), viewToWire(view)))
      );
    },
    async remove(id) {
      unwrapData(await client.delete(viewPath(id)));
    },
    async setDefault(tableId, scope, id) {
      const current = await listAll({ tableId, scope });
      const stale = current.filter(
        (v) => v.isDefault && v.id !== id && (currentUserId == null || v.owner === currentUserId)
      );
      const cleared = await Promise.allSettled(
        stale.map(
          (v) => client.patch(viewPath(v.id), { is_default: false })
        )
      );
      cleared.forEach((outcome, index) => {
        if (outcome.status === "rejected") {
          log.warn("could not clear a stale default view", {
            viewId: stale[index]?.id,
            error: String(outcome.reason)
          });
        }
      });
      if (id) {
        unwrapData(await client.patch(viewPath(id), { is_default: true }));
      }
    },
    async createShareLink(viewId, input) {
      const body = {
        target: input.target,
        password: input.password,
        expires_at: toSeconds(input.expiresAt),
        max_views: input.maxViews,
        data_mode: input.dataMode,
        catalog_resolution: input.catalogResolution,
        target_user_ids: input.targetUserIds,
        target_team_ids: input.targetTeamIds
      };
      return linkFromWire(unwrapData(await client.post(viewSharesPath(viewId), body)));
    },
    async listShareLinks(viewId) {
      const res = await client.get(
        viewSharesPath(viewId)
      );
      const data = unwrapData(res);
      const items = Array.isArray(data) ? data : data?.items ?? [];
      return items.map(linkFromWire);
    },
    async updateShareLink(token, input) {
      const body = {
        password: input.password,
        ...input.expiresAt !== void 0 ? { expires_at: toSeconds(input.expiresAt) } : {},
        max_views: input.maxViews,
        revoked: input.revoked
      };
      return linkFromWire(unwrapData(await client.patch(sharePath(token), body)));
    },
    async revokeShareLink(token) {
      unwrapData(await client.delete(sharePath(token)));
    },
    async resolveShare(token, password, reload) {
      const res = await client.get(resolvePath(token), {
        query: reload ? { reload: "true" } : void 0,
        headers: password ? { "X-Share-Password": password } : void 0
      });
      const raw = unwrapData(res);
      const resolved = raw.resolved ?? {};
      const link = raw.link;
      const cdnPath = resolved.payload_cdn_path ?? null;
      const fromResolver = resolved.payload_url ?? null;
      const payloadUrl = fromResolver ?? (cdnPath && cdnBaseUrl ? `${cdnBaseUrl}${cdnPath}` : null);
      return {
        payloadNeedsCredentials: !fromResolver && Boolean(payloadUrl),
        view: raw.view ?? {},
        link: link ? {
          token: link.token ?? token,
          target: link.target ?? "public",
          hasPassword: Boolean(link.has_password),
          viewCount: link.view_count ?? 0,
          payloadVisibility: link.payload_visibility ?? "private"
        } : void 0,
        dataMode: resolved.data_mode ?? raw.data_mode ?? "static",
        catalogResolution: resolved.catalog_resolution ?? void 0,
        // The overlay the resolver actually built. It was computed server-side
        // (an `owner_snapshot` link returns the owner's frozen tags) and then
        // dropped here, so the three catalog options resolved identically for
        // the viewer — a picker whose choice never left this function.
        catalog: resolved.catalog ?? null,
        // The inline snapshot lives on `resolved`, with everything else the
        // resolver decided — it was read off the envelope ROOT, where nothing
        // has ever been, so the inline path could not fire and every static
        // share paid a second round-trip for a payload already in hand.
        payload: resolved.payload ?? raw.payload ?? null,
        payloadUrl,
        payloadCdnPath: cdnPath,
        dynamicSeed: resolved.dynamic_seed ?? null,
        warnings: (resolved.warnings ?? raw.warnings ?? []).map((w) => ({
          code: w.code,
          message: w.message,
          resourceType: w.resource_type,
          hiddenCount: w.hidden_count ?? 0,
          sampleIds: w.sample_ids ?? []
        }))
      };
    }
  };
}
var log2 = createLogger(SAVED_VIEWS_PACKAGE);
function useSavedViews(options) {
  const { source, tableId, identity, onApply, autoLoad = true } = options;
  const [views, setViews] = React2.useState([]);
  const [status, setStatus] = React2.useState("idle");
  const [error, setError] = React2.useState(void 0);
  const [activeViewId, setActiveViewId] = React2.useState(null);
  const [truncated, setTruncated] = React2.useState(false);
  const sourceRef = React2.useRef(source);
  React2.useEffect(() => {
    sourceRef.current = source;
  }, [source]);
  const onApplyRef = React2.useRef(onApply);
  React2.useEffect(() => {
    onApplyRef.current = onApply;
  }, [onApply]);
  const identityKey = [
    identity.app,
    identity.customerId,
    identity.orgId,
    identity.tenantId,
    identity.userId
  ].join("|");
  const store = React2.useMemo(
    () => createScopedStorage(identity),
    // identity fields are captured by identityKey; the object identity may churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [identityKey]
  );
  const activeName = `${tableId}:${SV_ACTIVE_SUFFIX}`;
  const owner = identity.userId ?? null;
  const runRef = React2.useRef(0);
  const load = React2.useCallback(
    async (applyInitial) => {
      const runId = ++runRef.current;
      setStatus("loading");
      setError(void 0);
      try {
        const source_ = sourceRef.current;
        const page = source_.listWithMeta ? await source_.listWithMeta({ tableId }) : { views: await source_.list({ tableId }), truncated: false };
        if (runId !== runRef.current) return;
        const next = page.views;
        setViews(next);
        setTruncated(page.truncated);
        setStatus("ready");
        const persisted = store.getAt(activeName, "user", null);
        const resolved = persisted && next.find((v) => v.id === persisted) || next.find((v) => v.isDefault && v.owner === owner) || next.find((v) => v.isDefault) || null;
        setActiveViewId(resolved?.id ?? null);
        if (applyInitial && resolved) onApplyRef.current?.(resolved.state, resolved);
      } catch (err) {
        if (runId !== runRef.current) return;
        log2.error("failed to load saved views", { tableId, error: String(err) });
        setError(err instanceof Error ? err.message : String(err));
        setStatus("error");
      }
    },
    [tableId, store, activeName, owner]
  );
  React2.useEffect(() => {
    runRef.current += 1;
    setViews([]);
    setActiveViewId(null);
    if (autoLoad) void load(true);
  }, [autoLoad, load]);
  const refresh = React2.useCallback(() => void load(false), [load]);
  const apply = React2.useCallback(
    (id) => {
      const view = id ? views.find((v) => v.id === id) ?? null : null;
      setActiveViewId(view?.id ?? null);
      store.set(activeName, view?.id ?? null, "user");
      onApplyRef.current?.(view?.state ?? {}, view);
    },
    [views, store, activeName]
  );
  const save = React2.useCallback(
    async (name, state, scope, serving) => {
      const now = Date.now();
      const view = {
        id: newViewId(),
        name: name.trim(),
        tableId,
        scope,
        owner,
        state,
        ...serving ?? {},
        createdAt: now,
        updatedAt: now
      };
      try {
        const created = await sourceRef.current.create(view);
        notifySuccess(`Saved view "${created.name}"`);
        setActiveViewId(created.id);
        store.set(activeName, created.id, "user");
        await load(false);
        return created;
      } catch (err) {
        notifyError(err, { title: "Couldn't save the view" });
        return null;
      }
    },
    [tableId, owner, store, activeName, load]
  );
  const rename = React2.useCallback(
    async (id, name) => {
      const existing = views.find((v) => v.id === id);
      if (!existing) return;
      const prevViews = views;
      const trimmed = name.trim();
      setViews((prev) => prev.map((v) => v.id === id ? { ...v, name: trimmed } : v));
      try {
        await sourceRef.current.update({ ...existing, name: trimmed });
        await load(false);
      } catch (err) {
        setViews(prevViews);
        notifyError(err, { title: "Couldn't rename the view" });
      }
    },
    [views, load]
  );
  const remove = React2.useCallback(
    async (id) => {
      const prevViews = views;
      const prevActiveId = activeViewId;
      setViews((prev) => prev.filter((v) => v.id !== id));
      if (activeViewId === id) {
        setActiveViewId(null);
        store.set(activeName, null, "user");
      }
      try {
        await sourceRef.current.remove(id);
        notifySuccess("View deleted");
        await load(false);
      } catch (err) {
        setViews(prevViews);
        if (prevActiveId === id) {
          setActiveViewId(prevActiveId);
          store.set(activeName, prevActiveId, "user");
        }
        notifyError(err, { title: "Couldn't delete the view" });
      }
    },
    [views, activeViewId, store, activeName, load]
  );
  const setDefault = React2.useCallback(
    async (id, scope) => {
      const prevViews = views;
      setViews(
        (prev) => prev.map((v) => {
          if (v.scope !== scope) return v;
          if (v.id === id) return { ...v, isDefault: true };
          if (v.isDefault) return { ...v, isDefault: false };
          return v;
        })
      );
      try {
        await sourceRef.current.setDefault(tableId, scope, id);
        await load(false);
      } catch (err) {
        setViews(prevViews);
        notifyError(err, { title: "Couldn't update the default view" });
      }
    },
    [views, tableId, load]
  );
  const shareLink = React2.useCallback(
    (state, baseUrl) => {
      const url = baseUrl ?? (typeof location !== "undefined" ? location.href : "/");
      const result = applyViewStateToUrl(url, state);
      if (result.length > SV_MAX_SHARE_URL_LENGTH) {
        log2.warn("generated share link exceeds safe URL length limit", {
          tableId,
          length: result.length,
          limit: SV_MAX_SHARE_URL_LENGTH
        });
      }
      return result;
    },
    [tableId]
  );
  const activeView = React2.useMemo(
    () => views.find((v) => v.id === activeViewId) ?? null,
    [views, activeViewId]
  );
  return {
    views,
    status,
    error,
    truncated,
    activeViewId,
    activeView,
    refresh,
    apply,
    save,
    rename,
    remove,
    setDefault,
    shareLink
  };
}
var log3 = createLogger(`${SAVED_VIEWS_PACKAGE}:shares`);
function useShareLinks(options) {
  const { source, viewId, autoLoad = true } = options;
  const supported = typeof source.listShareLinks === "function";
  const [links, setLinks] = React2.useState([]);
  const [loading, setLoading] = React2.useState(false);
  const [error, setError] = React2.useState(void 0);
  const sourceRef = React2.useRef(source);
  React2.useEffect(() => {
    sourceRef.current = source;
  }, [source]);
  const load = React2.useCallback(async () => {
    if (!viewId || !sourceRef.current.listShareLinks) {
      setLinks([]);
      return;
    }
    setLoading(true);
    setError(void 0);
    try {
      setLinks(await sourceRef.current.listShareLinks(viewId));
    } catch (err) {
      log3.error("failed to load share links", { viewId, error: String(err) });
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [viewId]);
  React2.useEffect(() => {
    if (autoLoad) void load();
  }, [autoLoad, load]);
  const create = React2.useCallback(
    async (input) => {
      if (!viewId || !sourceRef.current.createShareLink) return null;
      try {
        const link = await sourceRef.current.createShareLink(viewId, input);
        notifySuccess("Share link created");
        setLinks((prev) => [link, ...prev]);
        return link;
      } catch (err) {
        notifyError(err, { title: "Couldn't create the share link" });
        return null;
      }
    },
    [viewId]
  );
  const update = React2.useCallback(
    async (token, input) => {
      if (!sourceRef.current.updateShareLink) return null;
      try {
        const link = await sourceRef.current.updateShareLink(token, input);
        setLinks((prev) => prev.map((l) => l.token === token ? link : l));
        return link;
      } catch (err) {
        notifyError(err, { title: "Couldn't update the share link" });
        return null;
      }
    },
    []
  );
  const revoke = React2.useCallback(
    async (token) => {
      if (!sourceRef.current.revokeShareLink) return;
      try {
        await sourceRef.current.revokeShareLink(token);
        notifySuccess("Share link revoked");
        setLinks((prev) => prev.map((l) => l.token === token ? { ...l, revoked: true } : l));
      } catch (err) {
        notifyError(err, { title: "Couldn't revoke the share link" });
      }
    },
    []
  );
  return {
    links,
    loading,
    error,
    refresh: () => void load(),
    create,
    update,
    revoke,
    supported
  };
}
var log4 = createLogger(`${SAVED_VIEWS_PACKAGE}:resolve-share`);
var SHARE_ERROR_CODES = {
  passwordRequired: "SHARE_LINK_PASSWORD_REQUIRED",
  authRequired: "SHARE_LINK_AUTH_REQUIRED",
  forbidden: "SHARE_LINK_FORBIDDEN",
  revoked: "SHARE_LINK_REVOKED",
  expired: "SHARE_LINK_EXPIRED",
  notFound: "SHARE_LINK_NOT_FOUND"
};
var EMPTY_WARNINGS = [];
function describeSnapshotGap(outcome) {
  if (outcome.dataMode !== "static") return null;
  if (outcome.hasPayload) return null;
  if (!outcome.hasPayloadUrl) {
    return "This shared view has no stored snapshot. It may have been shared before its data was frozen \u2014 ask the owner to re-share it.";
  }
  if (outcome.threw) {
    return "Couldn't reach the storage holding this view's data. Check your connection and reload.";
  }
  const status = outcome.status;
  if (status === void 0 || status >= 200 && status < 300) return null;
  if (status === 403) {
    return "Access to this view's data was refused (403). The link's download grant may have expired \u2014 reload the page, or ask the owner for a fresh link.";
  }
  if (status === 404) {
    return "This view's data is no longer stored (404). The owner may have deleted or re-saved the view.";
  }
  return `Couldn't load this view's data (HTTP ${status}).`;
}
function statusForCode(code) {
  switch (code) {
    case SHARE_ERROR_CODES.passwordRequired:
      return "password-required";
    case SHARE_ERROR_CODES.authRequired:
      return "auth-required";
    case SHARE_ERROR_CODES.forbidden:
      return "forbidden";
    case SHARE_ERROR_CODES.notFound:
      return "not-found";
    case SHARE_ERROR_CODES.revoked:
    case SHARE_ERROR_CODES.expired:
      return "expired";
    default:
      return "error";
  }
}
function useResolveShare(options) {
  const { source, token, fetchSnapshot = true, autoLoad = true } = options;
  const [status, setStatus] = React2.useState("idle");
  const [resolved, setResolved] = React2.useState(void 0);
  const [snapshot, setSnapshot] = React2.useState(void 0);
  const [snapshotError, setSnapshotError] = React2.useState(void 0);
  const [error, setError] = React2.useState(void 0);
  const [errorCode, setErrorCode] = React2.useState(void 0);
  const sourceRef = React2.useRef(source);
  React2.useEffect(() => {
    sourceRef.current = source;
  }, [source]);
  const passwordRef = React2.useRef(void 0);
  const runIdRef = React2.useRef(0);
  const run = React2.useCallback(
    async (reload2) => {
      const resolveShare = sourceRef.current.resolveShare;
      if (!token || !resolveShare) {
        setStatus("idle");
        return;
      }
      const runId = ++runIdRef.current;
      setStatus("loading");
      setError(void 0);
      setErrorCode(void 0);
      try {
        const result = await resolveShare(token, passwordRef.current, reload2);
        if (runId !== runIdRef.current) return;
        setResolved(result);
        let snap = result.payload ?? null;
        let status2;
        let threw = false;
        if (fetchSnapshot && !snap && result.dataMode === "static" && result.payloadUrl) {
          try {
            const res = await fetch(result.payloadUrl, {
              credentials: result.payloadNeedsCredentials ? "include" : "omit"
            });
            if (runId !== runIdRef.current) return;
            status2 = res.status;
            if (res.ok) snap = await res.json();
            else
              log4.warn("snapshot fetch non-2xx", {
                status: res.status,
                withCredentials: Boolean(result.payloadNeedsCredentials)
              });
          } catch (err) {
            threw = true;
            log4.warn("snapshot fetch failed", { error: String(err) });
          }
        }
        if (runId !== runIdRef.current) return;
        setSnapshot(snap);
        setSnapshotError(
          describeSnapshotGap({
            dataMode: result.dataMode,
            hasPayload: Boolean(snap),
            hasPayloadUrl: Boolean(result.payloadUrl),
            status: status2,
            threw
          }) ?? void 0
        );
        setStatus("ready");
      } catch (err) {
        if (runId !== runIdRef.current) return;
        const code = err instanceof ApiError ? err.code : void 0;
        const next = statusForCode(code);
        setErrorCode(code);
        setStatus(next);
        setError(err instanceof Error ? err.message : String(err));
        if (next === "error" || next === "not-found" || next === "expired") {
          log4.error("share resolve failed", { token, code, error: String(err) });
        }
      }
    },
    [token, fetchSnapshot]
  );
  React2.useEffect(() => {
    passwordRef.current = void 0;
    setResolved(void 0);
    setSnapshot(void 0);
    setSnapshotError(void 0);
    if (autoLoad && token) void run(false);
    else setStatus("idle");
  }, [autoLoad, token, run]);
  const submitPassword = React2.useCallback(
    (password) => {
      passwordRef.current = password;
      void run(false);
    },
    [run]
  );
  const reload = React2.useCallback(
    (opts) => {
      void run(Boolean(opts?.reload));
    },
    [run]
  );
  return {
    status,
    resolved,
    snapshot,
    snapshotError,
    warnings: resolved?.warnings ?? EMPTY_WARNINGS,
    error,
    errorCode,
    needsPassword: status === "password-required",
    needsAuth: status === "auth-required",
    submitPassword,
    reload
  };
}

// src/data/useGraphViews.ts
var GRAPHS_TABLE_ID = "graphs";
function useGraphViews(options) {
  const { source, identity, tableId = GRAPHS_TABLE_ID, onApply, autoLoad } = options;
  return useSavedViews({ source, identity, tableId, onApply, autoLoad });
}
function SaveCurrentViewForm({
  canShare,
  defaultScope,
  onSave,
  onCancel
}) {
  const [name, setName] = React2.useState("");
  const [scope, setScope] = React2.useState(defaultScope);
  const [busy, setBusy] = React2.useState(false);
  const scopes = canShare ? VIEW_SCOPES : ["user"];
  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      await onSave(trimmed, scope);
      onCancel();
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "space-y-4 py-2", children: [
      /* @__PURE__ */ jsx(Field, { label: "Name", children: /* @__PURE__ */ jsx(
        Input,
        {
          value: name,
          maxLength: SV_MAX_NAME_LENGTH,
          autoFocus: true,
          placeholder: "e.g. High-value pending",
          onChange: (event) => setName(event.target.value),
          onKeyDown: (event) => {
            if (event.key === "Enter") void submit();
          }
        }
      ) }),
      canShare && /* @__PURE__ */ jsx(
        Field,
        {
          label: "Visibility",
          hint: scope === "tenant" ? "Everyone in this tenant can see and apply this view." : "Only you can see this view.",
          ownsControl: true,
          children: /* @__PURE__ */ jsxs(Select, { value: scope, onValueChange: (value) => setScope(value), children: [
            /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
            /* @__PURE__ */ jsx(SelectContent, { children: scopes.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option, children: SCOPE_LABELS[option] }, option)) })
          ] })
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(DialogFooter, { children: [
      /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: onCancel, children: "Cancel" }),
      /* @__PURE__ */ jsx(Button, { onClick: () => void submit(), disabled: !name.trim() || busy, children: "Save" })
    ] })
  ] });
}
function SaveCurrentViewDialog({
  open,
  onOpenChange,
  canShare = true,
  defaultScope = "user",
  onSave
}) {
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsxs(DialogContent, { children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsx(DialogTitle, { children: "Save view" }),
      /* @__PURE__ */ jsx(DialogDescription, { children: "Save the current columns, filters, and sort so you can return to them in one click." })
    ] }),
    open && /* @__PURE__ */ jsx(
      SaveCurrentViewForm,
      {
        canShare,
        defaultScope,
        onSave,
        onCancel: () => onOpenChange(false)
      },
      String(open)
    )
  ] }) });
}
function scopeGroups(views) {
  const present = new Set(views.map((view) => view.scope));
  const ordered = [...VIEW_SCOPES];
  for (const scope of present) {
    if (!ordered.includes(scope)) ordered.push(scope);
  }
  return ordered.filter((scope) => present.has(scope));
}
function SavedViewsMenu({
  controller,
  onSaveCurrent,
  canSave = true,
  className
}) {
  const { views, activeView, activeViewId, apply, truncated } = controller;
  const [search, setSearch] = React2.useState("");
  const label = activeView ? activeView.name : "Views";
  const query = search.trim().toLowerCase();
  const filteredViews = query ? views.filter((view) => view.name.toLowerCase().includes(query)) : views;
  const showSearch = views.length >= 5 || truncated;
  return /* @__PURE__ */ jsxs(DropdownMenu, { onOpenChange: (open) => {
    if (!open) setSearch("");
  }, children: [
    /* @__PURE__ */ jsx(DropdownMenuTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", className: cn("gap-2", className), children: label }) }),
    /* @__PURE__ */ jsxs(DropdownMenuContent, { align: "start", className: "w-60", children: [
      showSearch && /* @__PURE__ */ jsx("div", { className: "p-1.5", onClick: (e) => e.stopPropagation(), children: /* @__PURE__ */ jsxs("div", { className: "relative", children: [
        /* @__PURE__ */ jsx(SearchIcon, { className: "pointer-events-none absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" }),
        /* @__PURE__ */ jsx(
          Input,
          {
            value: search,
            onChange: (e) => setSearch(e.target.value),
            onKeyDown: (e) => e.stopPropagation(),
            placeholder: "Filter views\u2026",
            className: "h-8 pl-7 text-xs"
          }
        )
      ] }) }),
      views.length === 0 && /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "font-normal text-muted-foreground", children: truncated ? "This surface has too many saved views to list. Filter above to narrow it." : "No saved views yet" }),
      truncated && views.length > 0 && !query && /* @__PURE__ */ jsx(DropdownMenuLabel, { className: "font-normal text-xs text-muted-foreground", children: "Showing the most recent \u2014 some views aren't listed" }),
      query && filteredViews.length === 0 && views.length > 0 && /* @__PURE__ */ jsxs(DropdownMenuLabel, { className: "font-normal text-xs text-muted-foreground", children: [
        'No views match "',
        search,
        '"'
      ] }),
      scopeGroups(filteredViews).map((scope) => {
        const scoped = filteredViews.filter((view) => view.scope === scope);
        if (scoped.length === 0) return null;
        return /* @__PURE__ */ jsxs(React2.Fragment, { children: [
          /* @__PURE__ */ jsx(DropdownMenuLabel, { children: SCOPE_LABELS[scope] }),
          scoped.map((view) => /* @__PURE__ */ jsxs(
            DropdownMenuItem,
            {
              onSelect: () => {
                apply(view.id);
                setSearch("");
              },
              className: "gap-2",
              children: [
                /* @__PURE__ */ jsx("span", { className: "flex h-3.5 w-3.5 items-center justify-center text-primary", children: view.id === activeViewId ? /* @__PURE__ */ jsx(CheckIcon, {}) : null }),
                /* @__PURE__ */ jsx("span", { className: "flex-1 truncate", children: view.name }),
                view.isDefault && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "Default" })
              ]
            },
            view.id
          ))
        ] }, scope);
      }),
      /* @__PURE__ */ jsx(DropdownMenuSeparator, {}),
      canSave && /* @__PURE__ */ jsx(DropdownMenuItem, { onSelect: onSaveCurrent, children: "Save current view\u2026" }),
      /* @__PURE__ */ jsx(
        DropdownMenuItem,
        {
          onSelect: () => {
            apply(null);
            setSearch("");
          },
          disabled: !activeViewId,
          children: "Reset"
        }
      )
    ] })
  ] });
}
function copyShareLink(link) {
  if (typeof navigator !== "undefined" && navigator.clipboard) {
    void navigator.clipboard.writeText(link).then(
      () => notifyInfo("Share link copied"),
      () => notifyInfo("Copy failed", "Copy the link from the address bar instead.")
    );
  }
}
function SavedViewsList({
  controller,
  canManageShared = true,
  canManagePersonal = true,
  canDeleteShared,
  showCopyLink = false,
  emptyDescription,
  renderBadge,
  className
}) {
  const { views, status, error, truncated, activeViewId, apply, remove, setDefault, shareLink } = controller;
  const [search, setSearch] = React2.useState("");
  if (status === "loading" || status === "idle") {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: [0, 1, 2].map((index) => /* @__PURE__ */ jsx(Skeleton, { className: "h-12 w-full" }, index)) });
  }
  if (status === "error") {
    return /* @__PURE__ */ jsx(ErrorState, { error: error ?? "Failed to load saved views", onRetry: controller.refresh, className });
  }
  if (views.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: truncated ? "Too many saved views to list" : "No saved views",
        description: truncated ? "This surface has more saved views than can be listed at once. Filter to narrow it." : emptyDescription ?? "Save the current columns, filters, and sort to reuse them later.",
        className
      }
    );
  }
  const query = search.trim().toLowerCase();
  const filteredViews = query ? views.filter((view) => view.name.toLowerCase().includes(query)) : views;
  const showSearch = views.length >= 4 || truncated;
  const canManage = (view) => view.scope === "user" ? canManagePersonal : canManageShared;
  const canDelete = (view) => view.scope === "user" ? canManagePersonal : canDeleteShared ?? canManageShared;
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-2", className), children: [
    showSearch && /* @__PURE__ */ jsxs("div", { className: "relative", children: [
      /* @__PURE__ */ jsx(SearchIcon, { className: "pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" }),
      /* @__PURE__ */ jsx(
        Input,
        {
          value: search,
          onChange: (event) => setSearch(event.target.value),
          placeholder: "Search saved views\u2026",
          className: "h-8 pl-8 text-xs"
        }
      )
    ] }),
    filteredViews.length === 0 && query ? /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No matching views",
        description: `No saved views match "${search}". Clear the search to see all views.`
      }
    ) : /* @__PURE__ */ jsxs("ul", { className: "divide-y rounded-md border", children: [
      truncated && !query && /* @__PURE__ */ jsx("li", { className: "px-3 py-2 text-xs text-muted-foreground", children: "Showing the most recent \u2014 some views aren't listed." }),
      filteredViews.map((view) => {
        const manageable = canManage(view);
        const deletable = canDelete(view);
        const isActive = view.id === activeViewId;
        return /* @__PURE__ */ jsxs("li", { className: cn("flex items-center gap-3 px-3 py-2", isActive && "bg-muted/50"), children: [
          /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              onClick: () => apply(view.id),
              className: "flex min-w-0 flex-1 items-center gap-2 text-left",
              children: [
                /* @__PURE__ */ jsx("span", { className: "truncate text-sm font-medium", children: view.name }),
                /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "shrink-0 text-xs", children: SCOPE_LABELS[view.scope] }),
                renderBadge?.(view),
                view.isDefault && /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "shrink-0 text-xs", children: "Default" })
              ]
            }
          ),
          /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-1", children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                "aria-label": view.isDefault ? "Unset default" : "Set as default",
                title: view.isDefault ? "Unset default" : "Set as default",
                disabled: !manageable,
                className: cn("h-8 w-8", view.isDefault && "text-amber-500"),
                onClick: () => void setDefault(view.isDefault ? null : view.id, view.scope),
                children: /* @__PURE__ */ jsx(StarIcon, { fill: view.isDefault ? "currentColor" : "none" })
              }
            ),
            showCopyLink && /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                "aria-label": "Copy share link",
                title: "Copy share link",
                className: "h-8 w-8",
                onClick: () => copyShareLink(shareLink(view.state)),
                children: /* @__PURE__ */ jsx(ShareIcon, {})
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "icon",
                "aria-label": "Delete view",
                title: "Delete view",
                disabled: !deletable,
                className: "h-8 w-8 text-muted-foreground hover:text-destructive",
                onClick: () => void remove(view.id),
                children: /* @__PURE__ */ jsx(TrashIcon, {})
              }
            )
          ] })
        ] }, view.id);
      })
    ] })
  ] });
}
var EXPIRY_OPTIONS = [
  { label: "Never", days: null },
  { label: "1 day", days: 1 },
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 }
];
function fullUrl(link, origin) {
  if (link.shareUrl && /^https?:\/\//.test(link.shareUrl)) return link.shareUrl;
  if (!link.shareUrl) return null;
  const base = origin ?? (typeof location !== "undefined" ? location.origin : "");
  if (!base) return null;
  return `${base}${link.shareUrl.startsWith("/") ? "" : "/"}${link.shareUrl}`;
}
function parseIdList(raw) {
  return raw.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
}
function PrincipalPicker({
  label,
  options,
  selected,
  onToggle,
  freeText,
  onFreeText,
  placeholder
}) {
  return /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
    /* @__PURE__ */ jsx(Label, { children: label }),
    options?.length ? /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1.5", children: options.map((option) => {
      const active = selected.includes(option.id);
      return /* @__PURE__ */ jsx("button", { type: "button", onClick: () => onToggle(option.id), children: /* @__PURE__ */ jsx(Badge, { variant: active ? void 0 : "outline", children: option.label }) }, option.id);
    }) }) : null,
    /* @__PURE__ */ jsx(
      Input,
      {
        value: freeText,
        placeholder,
        onChange: (event) => onFreeText(event.target.value)
      }
    )
  ] });
}
function ShareViewDialogContent({
  source,
  viewId,
  shareOrigin,
  userOptions,
  teamOptions,
  allowDynamic = true,
  onDone
}) {
  const { links, loading, create, update, revoke, supported } = useShareLinks({
    source,
    viewId,
    autoLoad: true
  });
  const [showRevoked, setShowRevoked] = React2.useState(false);
  const [target, setTarget] = React2.useState("public");
  const [dataMode, setDataMode] = React2.useState("static");
  const [catalogResolution, setCatalogResolution] = React2.useState("viewer");
  const [password, setPassword] = React2.useState("");
  const [expiryDays, setExpiryDays] = React2.useState(null);
  const [maxViews, setMaxViews] = React2.useState("");
  const [userIds, setUserIds] = React2.useState([]);
  const [teamIds, setTeamIds] = React2.useState([]);
  const [userIdText, setUserIdText] = React2.useState("");
  const [teamIdText, setTeamIdText] = React2.useState("");
  const [busy, setBusy] = React2.useState(false);
  const external = isExternalTarget(target);
  const needsUsers = target === "user";
  const needsTeams = target === "team";
  const audience = needsUsers ? Array.from(/* @__PURE__ */ new Set([...userIds, ...parseIdList(userIdText)])) : needsTeams ? Array.from(/* @__PURE__ */ new Set([...teamIds, ...parseIdList(teamIdText)])) : [];
  const audienceMissing = (needsUsers || needsTeams) && audience.length === 0;
  const toggleId = (id, ids, setIds) => {
    setIds(ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]);
  };
  const mint = async () => {
    if (busy || !viewId || audienceMissing) return;
    const resolvedUserIds = needsUsers ? audience : void 0;
    const resolvedTeamIds = needsTeams ? audience : void 0;
    setBusy(true);
    try {
      const expiresAt = expiryDays == null ? null : Date.now() + expiryDays * 864e5;
      const parsedMax = Number.parseInt(maxViews, 10);
      const resolvedDataMode = external ? "static" : dataMode;
      await create({
        target,
        dataMode: resolvedDataMode,
        catalogResolution,
        password: password.trim() || void 0,
        expiresAt,
        maxViews: Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : null,
        targetUserIds: resolvedUserIds,
        targetTeamIds: resolvedTeamIds
      });
      setPassword("");
      setMaxViews("");
    } finally {
      setBusy(false);
    }
  };
  const copy = async (link) => {
    const url = fullUrl(link, shareOrigin);
    if (!url) return;
    try {
      await navigator.clipboard?.writeText(url);
      notifySuccess("Link copied to clipboard");
    } catch {
    }
  };
  const restore = async (link) => {
    await update(link.token, { revoked: false });
  };
  const visibleLinks = showRevoked ? links : links.filter((l) => !l.revoked);
  const revokedCount = links.length - links.filter((l) => !l.revoked).length;
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(DialogHeader, { children: [
      /* @__PURE__ */ jsx(DialogTitle, { children: "Share view" }),
      /* @__PURE__ */ jsx(DialogDescription, { children: "Create a link to share this view. Public links open without sign-in; add a password or an expiry for tighter control." })
    ] }),
    !supported ? /* @__PURE__ */ jsx("p", { className: "py-4 text-sm text-muted-foreground", children: "Sharing isn't available for this view source." }) : /* @__PURE__ */ jsxs("div", { className: "space-y-4 py-2 min-w-0 w-full", children: [
      /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-2 gap-3", children: [
        /* @__PURE__ */ jsx(
          Field,
          {
            label: "Audience",
            hint: external ? "External \xB7 anyone with the link, no sign-in (opens on the public page)." : "Internal \xB7 the audience must sign in (opens on the private page).",
            ownsControl: true,
            children: /* @__PURE__ */ jsxs(
              Select,
              {
                value: target,
                onValueChange: (value) => {
                  const nextTarget = value;
                  setTarget(nextTarget);
                  if (isExternalTarget(nextTarget)) {
                    setDataMode("static");
                  }
                },
                children: [
                  /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
                  /* @__PURE__ */ jsx(SelectContent, { children: SHARE_TARGETS.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option, children: SHARE_TARGET_LABELS[option] }, option)) })
                ]
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Field,
          {
            label: "Data",
            hint: external ? "Public links use static snapshots frozen at share time." : allowDynamic ? "Choose whether viewers see a frozen snapshot or live queries." : "Only static snapshots are available for this view.",
            ownsControl: true,
            children: /* @__PURE__ */ jsxs(
              Select,
              {
                value: external ? "static" : dataMode,
                onValueChange: (value) => setDataMode(value),
                disabled: !allowDynamic || external,
                children: [
                  /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
                  /* @__PURE__ */ jsx(SelectContent, { children: VIEW_DATA_MODES.map((option) => /* @__PURE__ */ jsx(
                    SelectItem,
                    {
                      value: option,
                      disabled: option === "dynamic" && (!allowDynamic || external),
                      children: VIEW_DATA_MODE_LABELS[option]
                    },
                    option
                  )) })
                ]
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(Field, { label: "Catalog overlay", ownsControl: true, children: /* @__PURE__ */ jsxs(
          Select,
          {
            value: catalogResolution,
            onValueChange: (value) => setCatalogResolution(value),
            children: [
              /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
              /* @__PURE__ */ jsx(SelectContent, { children: CATALOG_RESOLUTIONS.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option, children: CATALOG_RESOLUTION_LABELS[option] }, option)) })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "Expires", ownsControl: true, children: /* @__PURE__ */ jsxs(
          Select,
          {
            value: String(expiryDays ?? "null"),
            onValueChange: (value) => setExpiryDays(value === "null" ? null : Number(value)),
            children: [
              /* @__PURE__ */ jsx(FormControl, { children: /* @__PURE__ */ jsx(SelectTrigger, { children: /* @__PURE__ */ jsx(SelectValue, {}) }) }),
              /* @__PURE__ */ jsx(SelectContent, { children: EXPIRY_OPTIONS.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: String(option.days ?? "null"), children: option.label }, option.label)) })
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "Password (optional)", children: /* @__PURE__ */ jsx(
          Input,
          {
            type: "password",
            value: password,
            placeholder: "No password",
            onChange: (event) => setPassword(event.target.value)
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "Max opens (optional)", children: /* @__PURE__ */ jsx(
          Input,
          {
            inputMode: "numeric",
            value: maxViews,
            placeholder: "Unlimited",
            onChange: (event) => setMaxViews(event.target.value.replace(/[^0-9]/g, ""))
          }
        ) })
      ] }),
      needsUsers && /* @__PURE__ */ jsx(
        PrincipalPicker,
        {
          label: "People",
          options: userOptions,
          selected: userIds,
          onToggle: (id) => toggleId(id, userIds, setUserIds),
          freeText: userIdText,
          onFreeText: setUserIdText,
          placeholder: "Add user ids (comma-separated)"
        }
      ),
      needsTeams && /* @__PURE__ */ jsx(
        PrincipalPicker,
        {
          label: "Teams",
          options: teamOptions,
          selected: teamIds,
          onToggle: (id) => toggleId(id, teamIds, setTeamIds),
          freeText: teamIdText,
          onFreeText: setTeamIdText,
          placeholder: "Add team ids (comma-separated)"
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
        /* @__PURE__ */ jsx(Button, { onClick: () => void mint(), disabled: busy || !viewId || audienceMissing, children: "Create link" }),
        audienceMissing ? /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: needsUsers ? "Pick at least one person \u2014 a link targeted at nobody can't be opened by anybody." : "Pick at least one team \u2014 a link targeted at no team can't be opened by anybody." }) : null
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "space-y-2 min-w-0 w-full", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
          /* @__PURE__ */ jsx(Label, { children: "Existing links" }),
          revokedCount > 0 && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "text-xs text-muted-foreground underline",
              onClick: () => setShowRevoked((value) => !value),
              children: showRevoked ? "Hide revoked" : `Show ${revokedCount} revoked`
            }
          )
        ] }),
        loading ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "Loading\u2026" }) : visibleLinks.length === 0 ? /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: revokedCount > 0 ? `No active share links (${revokedCount} revoked).` : "No share links yet." }) : /* @__PURE__ */ jsx("ul", { className: "space-y-2 min-w-0 w-full", children: visibleLinks.map((link) => {
          const url = fullUrl(link, shareOrigin);
          return /* @__PURE__ */ jsxs(
            "li",
            {
              className: "flex items-center justify-between gap-2 rounded-md border p-2 text-sm min-w-0 w-full",
              children: [
                /* @__PURE__ */ jsxs("div", { className: "flex-1 min-w-0 space-y-1", children: [
                  /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-1.5", children: [
                    /* @__PURE__ */ jsx(Badge, { variant: link.revoked ? "outline" : void 0, children: SHARE_TARGET_LABELS[link.target] }),
                    link.hasPassword && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Password" }),
                    link.revoked && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Revoked" })
                  ] }),
                  /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1 text-xs text-muted-foreground min-w-0 w-full", children: [
                    /* @__PURE__ */ jsx("span", { className: "flex-1 min-w-0 truncate block font-mono text-[11px]", title: url ?? void 0, children: url ?? /* @__PURE__ */ jsx("span", { className: "text-destructive", children: link.shareUrlUnavailable ?? "No viewer URL is configured for this audience yet." }) }),
                    /* @__PURE__ */ jsxs("span", { className: "shrink-0 whitespace-nowrap", children: [
                      "\xB7 ",
                      link.viewCount,
                      " open",
                      link.viewCount === 1 ? "" : "s"
                    ] })
                  ] })
                ] }),
                /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-1", children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      variant: "outline",
                      size: "sm",
                      disabled: !url,
                      onClick: () => void copy(link),
                      children: "Copy"
                    }
                  ),
                  link.revoked ? /* @__PURE__ */ jsx(
                    Button,
                    {
                      variant: "outline",
                      size: "sm",
                      onClick: () => void restore(link),
                      children: "Restore"
                    }
                  ) : /* @__PURE__ */ jsx(
                    Button,
                    {
                      variant: "outline",
                      size: "sm",
                      onClick: () => void revoke(link.token),
                      children: "Revoke"
                    }
                  )
                ] })
              ]
            },
            link.token
          );
        }) })
      ] })
    ] }),
    /* @__PURE__ */ jsx(DialogFooter, { children: /* @__PURE__ */ jsx(Button, { variant: "outline", onClick: onDone, children: "Done" }) })
  ] });
}
function ShareViewDialog({
  open,
  onOpenChange,
  source,
  viewId,
  shareOrigin,
  userOptions,
  teamOptions,
  allowDynamic = true
}) {
  return /* @__PURE__ */ jsx(Dialog, { open, onOpenChange, children: /* @__PURE__ */ jsx(DialogContent, { className: "sm:max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden", children: open && /* @__PURE__ */ jsx(
    ShareViewDialogContent,
    {
      source,
      viewId,
      shareOrigin,
      userOptions,
      teamOptions,
      allowDynamic,
      onDone: () => onOpenChange(false)
    },
    String(open)
  ) }) });
}
function phrase(warning) {
  if (warning.message) return warning.message;
  const count = warning.hiddenCount;
  const noun = warning.resourceType || "item";
  const plural = count === 1 ? noun : `${noun}s`;
  return `${count} ${plural} in this view ${count === 1 ? "is" : "are"} not visible to you.`;
}
function AccessWarningBanner({
  warnings,
  className
}) {
  if (!warnings?.length) return null;
  return /* @__PURE__ */ jsxs(Alert, { variant: "warning", className, children: [
    /* @__PURE__ */ jsx(AlertTitle, { children: "Some data is hidden" }),
    /* @__PURE__ */ jsx(AlertDescription, { children: /* @__PURE__ */ jsx("ul", { className: "list-disc space-y-1 pl-4", children: warnings.map((warning, index) => /* @__PURE__ */ jsx("li", { children: phrase(warning) }, `${warning.code}-${index}`)) }) })
  ] });
}
var DEFAULT_FILTER_CODEC = {
  toRules: (filters) => {
    const rules = [];
    for (const filter of filters) {
      if (Array.isArray(filter.value)) {
        const values = filter.value.map(String).filter(Boolean);
        if (values.length) rules.push({ field: filter.id, op: "in", value: values });
      } else if (typeof filter.value === "string" && filter.value !== "") {
        rules.push({ field: filter.id, op: "contains", value: filter.value });
      }
    }
    return rules;
  },
  fromRules: (rules) => {
    const filters = [];
    for (const rule of rules ?? []) {
      if (rule.op === "in") {
        const values = Array.isArray(rule.value) ? rule.value.map(String).filter(Boolean) : [];
        if (values.length) filters.push({ id: rule.field, value: values });
      } else if (rule.op === "contains" && rule.value != null && rule.value !== "") {
        filters.push({ id: rule.field, value: String(rule.value) });
      }
    }
    return filters;
  }
};
function visibilityToColumnPrefs(visibility, columnIds, columnOrder = []) {
  const known = new Set(columnIds);
  const named = columnOrder.filter((id) => known.has(id));
  const seen = new Set(named);
  const ordered = [...named, ...columnIds.filter((id) => !seen.has(id))];
  return ordered.map((id) => ({ id, visible: visibility[id] !== false }));
}
function columnPrefsToVisibility(prefs) {
  const visibility = {};
  for (const pref of prefs ?? []) visibility[pref.id] = pref.visible !== false;
  return visibility;
}
function columnPrefsToOrder(prefs) {
  return (prefs ?? []).map((pref) => pref.id);
}
function sortingToSortRules(sorting) {
  return sorting.map((s) => ({ field: s.id, direction: s.desc ? "desc" : "asc" }));
}
function sortRulesToSorting(rules) {
  return (rules ?? []).map((r) => ({ id: r.field, desc: r.direction === "desc" }));
}
function useTableViews(options) {
  const {
    source,
    tableId,
    identity,
    columnIds,
    filterCodec = DEFAULT_FILTER_CODEC,
    autoLoad,
    defaultDensity = "comfortable"
  } = options;
  const [columnVisibility, setColumnVisibility] = React2.useState({});
  const [columnOrder, setColumnOrder] = React2.useState([]);
  const [columnFilters, setColumnFilters] = React2.useState([]);
  const [sorting, setSorting] = React2.useState([]);
  const [density, setDensity] = React2.useState(defaultDensity);
  const codecRef = React2.useRef(filterCodec);
  React2.useEffect(() => {
    codecRef.current = filterCodec;
  });
  const views = useSavedViews({
    source,
    tableId,
    identity,
    ...autoLoad !== void 0 ? { autoLoad } : {},
    onApply: (state) => {
      setColumnVisibility(columnPrefsToVisibility(state.columns));
      setColumnOrder(columnPrefsToOrder(state.columns));
      setColumnFilters(codecRef.current.fromRules(state.filters));
      setSorting(sortRulesToSorting(state.sort));
      if (state.density) setDensity(state.density);
    }
  });
  const onColumnVisibilityChange = React2.useCallback(
    (updater) => setColumnVisibility((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const onColumnOrderChange = React2.useCallback(
    (updater) => setColumnOrder((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const onColumnFiltersChange = React2.useCallback(
    (updater) => setColumnFilters((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const onSortingChange = React2.useCallback(
    (updater) => setSorting((prev) => typeof updater === "function" ? updater(prev) : updater),
    []
  );
  const captureState = React2.useCallback(
    () => ({
      columns: visibilityToColumnPrefs(columnVisibility, columnIds, columnOrder),
      filters: filterCodec.toRules(columnFilters),
      sort: sortingToSortRules(sorting),
      density
    }),
    [columnVisibility, columnIds, columnOrder, filterCodec, columnFilters, sorting, density]
  );
  const saveCurrent = React2.useCallback(
    (name, scope) => views.save(name, captureState(), scope),
    [views, captureState]
  );
  const serverFilters = React2.useMemo(
    () => filterCodec.toRules(columnFilters),
    [filterCodec, columnFilters]
  );
  return {
    views,
    columnVisibility,
    onColumnVisibilityChange,
    columnOrder,
    onColumnOrderChange,
    columnFilters,
    onColumnFiltersChange,
    sorting,
    onSortingChange,
    density,
    setDensity,
    serverFilters,
    captureState,
    saveCurrent
  };
}
var INERT_SOURCE = {
  list: async () => [],
  create: async (view) => view,
  update: async (view) => view,
  remove: async () => {
  },
  setDefault: async () => {
  }
};
function useTableSavedViews(options) {
  const {
    enabled = false,
    tableId,
    columnIds,
    filterCodec,
    canShare = true,
    canSave = true,
    defaultDensity
  } = options;
  const context = useOptionalSavedViews();
  const active = enabled && context != null;
  const [saveOpen, setSaveOpen] = React2.useState(false);
  const views = useTableViews({
    source: context?.source ?? INERT_SOURCE,
    tableId,
    identity: context?.identity ?? {},
    columnIds,
    autoLoad: active,
    ...filterCodec ? { filterCodec } : {},
    ...defaultDensity ? { defaultDensity } : {}
  });
  const exportContext = {
    tableId,
    ...views.views.activeViewId ? { viewId: views.views.activeViewId } : {},
    ...views.serverFilters.length > 0 ? { filters: { rules: views.serverFilters } } : {}
  };
  if (!active) {
    return { active: false, controlled: {}, toolbar: null, density: void 0, exportContext };
  }
  return {
    active: true,
    controlled: {
      columnVisibility: views.columnVisibility,
      onColumnVisibilityChange: views.onColumnVisibilityChange,
      columnOrder: views.columnOrder,
      onColumnOrderChange: views.onColumnOrderChange,
      columnFilters: views.columnFilters,
      onColumnFiltersChange: views.onColumnFiltersChange,
      sorting: views.sorting,
      onSortingChange: views.onSortingChange
    },
    density: views.density,
    exportContext,
    toolbar: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        SavedViewsMenu,
        {
          controller: views.views,
          canSave,
          onSaveCurrent: () => setSaveOpen(true)
        }
      ),
      /* @__PURE__ */ jsx(
        SaveCurrentViewDialog,
        {
          open: saveOpen,
          onOpenChange: setSaveOpen,
          canShare,
          onSave: async (name, scope) => {
            await views.saveCurrent(name, scope);
          }
        }
      )
    ] })
  };
}

export { AccessWarningBanner, CATALOG_RESOLUTIONS, CATALOG_RESOLUTION_LABELS, DEFAULT_FILTER_CODEC, GRAPHS_TABLE_ID, SAVED_VIEWS_PACKAGE, SCOPE_LABELS, SHARE_ERROR_CODES, SHARE_TARGETS, SHARE_TARGET_LABELS, SV_ACTIVE_SUFFIX, SV_DRAFT_SUFFIX, SV_MAX_NAME_LENGTH, SV_MAX_SHARE_URL_LENGTH, SV_URL_PARAM, SaveCurrentViewDialog, SavedViewsList, SavedViewsMenu, SavedViewsProvider, ShareViewDialog, VIEW_DATA_MODES, VIEW_DATA_MODE_LABELS, VIEW_SCOPES, applyViewStateToUrl, columnPrefsToVisibility, createApiSavedViewsSource, createInMemorySavedViewsSource, decodeViewState, describeSnapshotGap, emptyView, encodeViewState, isEmptyState, isExternalTarget, isViewScope, newViewId, readShareTokenFromUrl, readViewStateFromUrl, sortRulesToSorting, sortingToSortRules, useGraphViews, useOptionalSavedViews, useResolveShare, useSavedViews, useShareLinks, useTableSavedViews, useTableViews, visibilityToColumnPrefs };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map